import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Divider,
  CircularProgress,
  Snackbar,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';

interface PaymentSettings {
  bank_transfer_account_name: string;
  bank_transfer_account_number: string;
  bank_transfer_bank_name: string;
  bank_transfer_routing_number: string;
  bank_transfer_instructions: string;
}

const PaymentSettingsManager: React.FC = () => {
  const [settings, setSettings] = useState<PaymentSettings>({
    bank_transfer_account_name: '',
    bank_transfer_account_number: '',
    bank_transfer_bank_name: '',
    bank_transfer_routing_number: '',
    bank_transfer_instructions: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/payment`);
      
      if (response.data.success) {
        setSettings(prevSettings => ({
          ...prevSettings,
          ...response.data.settings
        }));
      }
    } catch (error: any) {
      console.error('Failed to load settings:', error);
      setError('Failed to load payment settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (key: keyof PaymentSettings) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({
      ...prev,
      [key]: event.target.value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Convert settings object to array format expected by API
      const settingsArray = Object.entries(settings).map(([key, value]) => ({
        key,
        value
      }));
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/settings/payment`,
        { settings: settingsArray },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        setSuccessMessage('Payment settings saved successfully!');
      }
    } catch (error: any) {
      console.error('Failed to save settings:', error);
      if (error.response?.status === 401) {
        setError('You are not authorized to update settings. Please login as an admin.');
      } else {
        setError('Failed to save settings. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInitializeDefaults = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/settings/initialize`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        setSuccessMessage('Default settings initialized successfully!');
        await loadSettings(); // Reload settings
      }
    } catch (error: any) {
      console.error('Failed to initialize settings:', error);
      setError('Failed to initialize default settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Payment Settings
      </Typography>
      
      <Typography variant="body1" color="textSecondary" paragraph>
        Configure bank transfer instructions and payment details that will be displayed to users.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Bank Transfer Information
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              sx={{ flex: 1, minWidth: 250 }}
              label="Account Name"
              value={settings.bank_transfer_account_name}
              onChange={handleInputChange('bank_transfer_account_name')}
              variant="outlined"
              placeholder="e.g., BSMMU Pathology Alumni Association"
            />
            
            <TextField
              sx={{ flex: 1, minWidth: 250 }}
              label="Account Number"
              value={settings.bank_transfer_account_number}
              onChange={handleInputChange('bank_transfer_account_number')}
              variant="outlined"
              placeholder="e.g., 123-456-789-012"
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              sx={{ flex: 1, minWidth: 250 }}
              label="Bank Name"
              value={settings.bank_transfer_bank_name}
              onChange={handleInputChange('bank_transfer_bank_name')}
              variant="outlined"
              placeholder="e.g., Dutch Bangla Bank Limited"
            />
            
            <TextField
              sx={{ flex: 1, minWidth: 250 }}
              label="Routing Number"
              value={settings.bank_transfer_routing_number}
              onChange={handleInputChange('bank_transfer_routing_number')}
              variant="outlined"
              placeholder="e.g., 090260012"
            />
          </Box>
          
          <TextField
            fullWidth
            label="Transfer Instructions"
            value={settings.bank_transfer_instructions}
            onChange={handleInputChange('bank_transfer_instructions')}
            variant="outlined"
            multiline
            rows={3}
            placeholder="Additional instructions for users making bank transfers..."
          />
        </Stack>
        
        <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving}
            sx={{ minWidth: 150 }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSettings}
            disabled={saving}
          >
            Reload
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleInitializeDefaults}
            disabled={saving}
            color="secondary"
          >
            Initialize Defaults
          </Button>
        </Box>
      </Paper>

      {/* Preview Section */}
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Preview - How it appears to users
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <Alert severity="info">
          <Typography variant="subtitle2" gutterBottom>
            Bank Transfer Instructions:
          </Typography>
          <Typography variant="body2">
            Account Name: {settings.bank_transfer_account_name || 'Not set'}<br />
            Account Number: {settings.bank_transfer_account_number || 'Not set'}<br />
            Bank: {settings.bank_transfer_bank_name || 'Not set'}<br />
            Routing Number: {settings.bank_transfer_routing_number || 'Not set'}
            {settings.bank_transfer_instructions && (
              <>
                <br /><br />
                {settings.bank_transfer_instructions}
              </>
            )}
          </Typography>
        </Alert>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage(null)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentSettingsManager;
